export type Guide = {
    number: string
    slug: string
    title: string
    hook: string
    rarity: 'Common' | 'Rare' | 'Holo'
    minutes: number
    level: 'Easy' | 'Medium' | 'Spicy'
    tone: string
    ink: string
    preview: Array<string>
    steps: Array<string>
}

export const guides: Array<Guide> = [
    {
        number: '001',
        slug: 'the-3-second-hook',
        title: 'The 3 second hook',
        hook: 'Five openings that stop a thumb mid-scroll.',
        rarity: 'Holo',
        minutes: 6,
        level: 'Easy',
        tone: '#ff5c00',
        ink: '#1e1e1e',
        preview: ['Start at the end', 'The wrong answer, said with confidence', 'Text that changes the picture'],
        steps: [
            'Watch your edit and find the most satisfying moment. Put a slice of it in the first second.',
            'Write on-screen text of seven words or fewer that changes what the viewer thinks they are seeing.',
            'Cut three versions with different first seconds and post them a week apart.',
            'Compare how many people are still watching at five seconds, not the total views.',
            'Keep the winning opening as a template for the next month.'
        ]
    },
    {
        number: '002',
        slug: 'a-month-in-an-afternoon',
        title: 'A month of content in one afternoon',
        hook: 'The batching plan we give every new client.',
        rarity: 'Rare',
        minutes: 9,
        level: 'Medium',
        tone: '#1e1e1e',
        ink: '#f2efe9',
        preview: ['One hour of thinking first', 'Film in blocks, not posts', 'Edit everything in one sitting'],
        steps: [
            'A week before, write twelve one-sentence post ideas from your dates, your questions and your series.',
            'Block four hours. Film the four series episodes first, then the answers, then the b-roll.',
            'Shoot stills by the same window so the feed looks like one brand.',
            'Edit in one evening with one text template and one caption colour.',
            'Schedule the month and keep thirty minutes a week for replies and anything topical.'
        ]
    },
    {
        number: '003',
        slug: 'captions-that-get-replies',
        title: 'Captions that get replies',
        hook: 'Small changes that turn lurkers into talkers.',
        rarity: 'Rare',
        minutes: 5,
        level: 'Easy',
        tone: '#ffe27a',
        ink: '#1e1e1e',
        preview: ['Write line one for search', 'Ask a five word question', 'Reply within the first hour'],
        steps: [
            'Write the first line the way people would type it into the search bar.',
            'Put the joke, the story or the detail in line two.',
            'End with a question people can answer in five words or fewer.',
            'Reply to the first ten comments within an hour of posting.',
            'Pin the best reply so newcomers can see the conversation is welcome.'
        ]
    },
    {
        number: '004',
        slug: 'the-series-starter-kit',
        title: 'The series starter kit',
        hook: 'How to build a format people come back for.',
        rarity: 'Holo',
        minutes: 8,
        level: 'Medium',
        tone: '#ebe6dd',
        ink: '#1e1e1e',
        preview: ['Give it a name and a day', 'Strict format, loose story', 'Commit for twelve weeks'],
        steps: [
            'Pick one thing you can show every week without running out: a repair, a recipe, a regular, a question.',
            'Give it a name, a title card and a fixed day.',
            'Keep the length, the framing and the opening line identical every episode.',
            'Change the story inside the format every time.',
            'Judge it after twelve weeks, not three.'
        ]
    },
    {
        number: '005',
        slug: 'posting-times-without-myths',
        title: 'Posting times, without the myths',
        hook: 'Find your quiet slots in ten minutes.',
        rarity: 'Common',
        minutes: 4,
        level: 'Easy',
        tone: '#f8f6f2',
        ink: '#1e1e1e',
        preview: ['Check when your people are online', 'Find when rivals go quiet', 'Pick three slots, stick to them'],
        steps: [
            'Open your analytics and note the three busiest hours for your followers.',
            'Scroll your five closest competitors and note when they usually post.',
            'Circle the hours where your people are online and your rivals are not.',
            'Post in those slots for a month without changing anything else.',
            'Spend the time you saved on better ideas, which matter far more.'
        ]
    },
    {
        number: '006',
        slug: 'the-founder-on-camera',
        title: 'The founder on camera',
        hook: 'For people who would rather not be on camera.',
        rarity: 'Rare',
        minutes: 7,
        level: 'Spicy',
        tone: '#ff5c00',
        ink: '#1e1e1e',
        preview: ['Talk to one person, not a crowd', 'Hold something in your hands', 'Three takes, then stop'],
        steps: [
            'Write the one sentence you want people to remember, then say it to a friend first.',
            'Hold something while you talk: the product, a tool, a mug. Hands calm the nerves.',
            'Film at eye level by a window, closer than feels comfortable.',
            'Do three takes. Pick the one where you sound most like yourself, not the most polished.',
            'Post it before you can talk yourself out of it.'
        ]
    },
    {
        number: '007',
        slug: 'read-your-numbers-in-ten-minutes',
        title: 'Read your numbers in ten minutes',
        hook: 'A Monday checklist that ends in decisions.',
        rarity: 'Common',
        minutes: 5,
        level: 'Medium',
        tone: '#1e1e1e',
        ink: '#ffe27a',
        preview: ['Replies per thousand views', 'Saves and sends', 'One sequel, one retirement'],
        steps: [
            'Pull last week posts and note views, replies, saves and sends for each.',
            'Work out replies per thousand views. That is your headline number.',
            'Find the post with the most saves and sends. That one gets a sequel.',
            'Find the post that did nothing for the second week running. That one retires.',
            'Write down one thing to test next week, and only one.'
        ]
    },
    {
        number: '008',
        slug: 'the-no-list',
        title: 'The no list',
        hook: 'Build a brand voice by banning words.',
        rarity: 'Holo',
        minutes: 6,
        level: 'Spicy',
        tone: '#ffe27a',
        ink: '#1e1e1e',
        preview: ['Collect the lazy words', 'Swap abstract for specific', 'One person signs off'],
        steps: [
            'Read your last twenty captions out loud and circle every word you would not say to a customer.',
            'Put those words on a list. That is your no list.',
            'For each one, write a specific detail only you could say instead.',
            'Steal three phrases your customers use in reviews and messages.',
            'Pick one person to own the voice and give them the final say.'
        ]
    }
]
